import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import * as _ from 'lodash';
import {
  combineLatest,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { Dao } from '../dao';
import { DataLoaderAuthentified } from '../data-loader-authentified';
import { LoanDao } from '../loan/loan.dao';
import CreateEstateDTO from './dto/create-estate.dto';
import CreateTenantDTO from './dto/create-tenant.dto';
import { EstatePersisted, FiscalLaw } from './estate.interface';

@Injectable({
  providedIn: 'root',
})
export class EstateDao extends Dao {
  private estates: DataLoaderAuthentified<any> =
    new DataLoaderAuthentified<any>(
      this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(),
      user => this.httpApi.get(`/user/${user.id}/realestate/global`),
    );

  private personalUses: DataLoaderAuthentified<any> =
    new DataLoaderAuthentified<any>(
      this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(),
      user =>
        this.httpApi.get(`/user/${user.id}/realestate/personalUse/global`),
    );

  private lmpLmnp: DataLoaderAuthentified<any> =
    new DataLoaderAuthentified<any>(
      this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(),
      user => this.httpApi.get(`/user/${user.id}/realestate/lmplmnp/global`),
    );

  private unfurnished: DataLoaderAuthentified<any> =
    new DataLoaderAuthentified<any>(
      this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(),
      user =>
        this.httpApi.get(`/user/${user.id}/realestate/unfurnished/global`),
    );

  private scpi: DataLoaderAuthentified<any> = new DataLoaderAuthentified<any>(
    this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(),
    user => this.httpApi.get(`/user/${user.id}/realestate/scpi/global`),
  );

  public onReset: Subject<void> = new Subject<void>();

  constructor(
    httpApi: HttpApiService,
    private loanDao: LoanDao,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }

  /* Routes */

  getEstates(): Observable<any> {
    return this.estates.get();
  }

  getPersonalUses(): Observable<any> {
    return this.personalUses.get();
  }

  getPersonalUsesOnce(): Observable<any> {
    return this.personalUses.current$();
  }

  getScpiDetails(userId, scpiId): Observable<any> {
    return this.httpApi.get(`/user/${userId}/realestate/scpi/${scpiId}`);
  }

  getUnfurnishedDetails(userId, unfurnishedId): Observable<any> {
    return this.httpApi.get(
      `/user/${userId}/realestate/unfurnished/${unfurnishedId}`,
    );
  }

  getPersonalUse(userId, residId): Observable<any> {
    return this.httpApi.get(
      `/user/${userId}/realestate/personalUse/${residId}/global`,
    );
  }

  getLmpLmnps(): Observable<any> {
    return this.lmpLmnp.get();
  }

  getLmpLmnpsOnce(): Observable<any> {
    return this.lmpLmnp.current$();
  }

  getLmpLmnp(userId, residId): Observable<any> {
    return this.httpApi.get(
      `/user/${userId}/realestate/lmplmnp/${residId}/global`,
    );
  }

  getUnfurnishedGlobal(): Observable<any> {
    return this.unfurnished.get();
  }

  getUnfurnishedGlobalOnce(): Observable<any> {
    return this.unfurnished.current$();
  }

  getUnfurnished(userId, residId): Observable<any> {
    return this.httpApi.get(
      `/user/${userId}/realestate/unfurnished/${residId}/global`,
    );
  }

  getScpis(): Observable<any> {
    return this.scpi.get();
  }

  getScpisOnce(): Observable<any> {
    return this.scpi.current$();
  }

  getScpi(accountId, residId, ownershipType, paymentType): Observable<any> {
    return this.httpApi.get(
      `/accounts/${accountId}/realestate/scpi/product/${residId}?ownership-type=${ownershipType}&payment-type=${paymentType}`,
    );
  }

  getScpiDashboard(accountId, scpiId): Observable<any> {
    return this.httpApi.get(
      `/accounts/${accountId}/realestate/scpi/${scpiId}/dashboard`,
    );
  }

  postEstate(
    entity: any,
    reset: boolean = true,
    onPersisted?: (persisted: EstatePersisted) => void,
  ): Observable<any> {
    let omitFields = ['lastEstimation', 'lastValue', 'lastValueRealEstate'];
    let dto: any = _.cloneDeep(_.omit(entity, omitFields));
    if (entity['removeLoan'] && entity.loans[0]) {
      this.removeLoanToEstate(entity);
      dto.loans = undefined;
    }
    let sub: Observable<any> = this.editOrCreate(dto);
    return sub.pipe(
      tap(estate =>
        onPersisted?.({ id: estate.id, investmentId: estate.investmentId }),
      ),
      this.treatExtras(reset),
      tap(() => {
        if (reset) this?.reset();
      }),
    );
  }

  postRealestateExpense(
    userId: any,
    estateType: string,
    estateId: any,
    body: any,
    reset: boolean = true,
  ): Observable<any> {
    return this.httpApi
      .post(
        `/user/${userId}/realestate/${estateType}/${estateId}/expense`,
        body,
      )
      .pipe(
        tap(() => {
          if (reset) this?.reset();
        }),
      );
  }

  removeLoanToEstate(entity) {
    this.loanDao.deleteLoan(entity.accountId, entity.loans[0].id).subscribe();
  }

  deleteRealestateExpense(
    userId: any,
    estateType: string,
    estateId: any,
    expenseId: any,
  ): Observable<any> {
    return this.httpApi.delete(
      `/user/${userId}/realestate/${estateType}/${estateId}/expense/${expenseId}`,
    );
  }

  getRealestateRents(
    userId: any,
    estateType: string,
    estateId: any,
  ): Observable<any> {
    return this.httpApi.get(
      `/user/${userId}/realestate/${estateType}/${estateId}/rent`,
    );
  }

  postRealestateRent(
    userId: any,
    estateType: string,
    estateId: any,
    body: any,
    reset: boolean = true,
  ): Observable<any> {
    return this.httpApi
      .post(`/user/${userId}/realestate/${estateType}/${estateId}/rent`, body)
      .pipe(
        tap(() => {
          if (reset) this?.reset();
        }),
      );
  }

  deleteRealestateRent(
    userId: any,
    residId: any,
    type: string,
    rentId: number,
    reset: boolean = true,
  ): Observable<any> {
    return this.httpApi
      .delete(`/user/${userId}/realestate/${type}/${residId}/rent/${rentId}`)
      .pipe(
        tap(() => {
          if (reset) this?.reset();
        }),
      );
  }

  postRealestateTenant(
    userId: any,
    estateType: string,
    estateId: any,
    rentId: number,
    body: CreateTenantDTO,
    reset: boolean = true,
  ): Observable<any> {
    return this.httpApi
      .post(
        `/user/${userId}/realestate/${estateType}/${estateId}/rent/${rentId}/tenant`,
        body,
      )
      .pipe(
        tap(() => {
          if (reset) this?.reset();
        }),
      );
  }

  postFiscalLaw(
    userId: any,
    estateType: string,
    estateId: any,
    body: FiscalLaw,
    reset: boolean = true,
  ): Observable<any> {
    return this.httpApi
      .post(
        `/user/${userId}/realestate/${estateType}/${estateId}/fiscalLaw`,
        body,
      )
      .pipe(
        tap(() => {
          if (reset) this?.reset();
        }),
      );
  }

  convertRentalType(
    userId: any,
    sourceTypeUrl: 'unfurnished' | 'lmplmnp',
    estateId: any,
    body: { targetType: string; targetFiscalLaw?: string },
    reset: boolean = true,
  ): Observable<any> {
    return this.httpApi
      .post(
        `/user/${userId}/realestate/${sourceTypeUrl}/${estateId}/conversion`,
        body,
      )
      .pipe(
        tap(() => {
          if (reset) this?.reset();
        }),
      );
  }

  deleteEstate(entity: any, reset: boolean = true): Observable<any> {
    let typeUrl = _.camelCase(entity.typeUrl || entity.productType);
    return this.command(user =>
      this.httpApi.delete(
        `/user/${entity.userId || user.id}/realestate/${typeUrl}/${entity.id}`,
      ),
    ).pipe(
      tap(() => {
        if (reset) this?.reset();
      }),
    );
  }

  reset() {
    this.estates?.reset();
    this.personalUses?.reset();
    this.lmpLmnp?.reset();
    this.unfurnished?.reset();
    this.scpi?.reset();
    this.loanDao?.reset();
    this.onReset?.next();
  }

  /* logic */
  createEstate(entity: CreateEstateDTO): Observable<any> {
    //Create subscription
    return this.httpApi
      .post(`/user/${entity.userId}/realestate/${entity.typeUrl}`, entity)
      .pipe(
        map(res => res['data']),
        // Validate subscription
        switchMap(subscriptionId =>
          this.httpApi.get(
            `/user/${entity.userId}/realestate/${entity.typeUrl}/${subscriptionId}`,
          ),
        ),
        map(res => {
          if (entity.loans && entity.loans[0]) {
            if (!entity.loans[0].label) entity.loans[0].label = entity.label;
            entity.loans[0].investmentIds = [];
            entity.loans[0].investmentIds.push(res.data.investmentId);
          }
          entity.id = res['data'].id;
          entity.investmentId = res['data'].investmentId;
          return entity;
        }),
      );
  }

  editEstate(entity): Observable<any> {
    let dto = _.omit(_.cloneDeep(entity), ['id']);
    return this.httpApi
      .post(
        `/user/${entity.userId}/realestate/${entity.typeUrl}/${entity.id}`,
        dto,
      )
      .pipe(
        map(res => res['data']),
        switchMap(subscriptionId =>
          this.httpApi.get(
            `/user/${entity.userId}/realestate/${entity.typeUrl}/${subscriptionId}`,
          ),
        ),
        map(res => {
          if (entity.loans && entity.loans[0]) {
            if (!entity.loans[0].label) entity.loans[0].label = entity.label;
            entity.loans[0].investmentIds = [];
            entity.loans[0].investmentIds.push(res.data.investmentId);
          }
          entity.investmentId = res['data'].investmentId;
          return entity;
        }),
      );
  }

  editOrCreate(entity): Observable<any> {
    // Enforce the use of the main user id until couple with different data for each will be fully implemented
    return this.commandAsMainUser(user => {
      const payload = {
        ...entity,
        userId: user.id,
      };

      return !payload.id
        ? this.createEstate(payload)
        : this.editEstate(payload);
    });
  }

  treatExtras(reset: boolean = true) {
    return switchMap(entity => {
      const observables = [];
      observables.push(
        this.createAndBindExpensesToEstate(entity, reset),
        this.createAndBindLoanToEstate(entity, reset),
        this.locationEstateTreatment(entity, reset),
      );
      return combineLatest(observables).pipe(map(() => entity));
    });
  }

  createAndBindExpensesToEstate(
    entity,
    reset: boolean = true,
  ): Observable<any> {
    return forkJoin(
      entity.expenses && entity.expenses.length
        ? entity.expenses.map(expense =>
            this.postRealestateExpense(
              entity.userId,
              entity.typeUrl,
              entity.id,
              expense,
              reset,
            ),
          )
        : of(true),
    );
  }

  // change subscriptionId by { investmentIds : [{linkedInvestmentId}] ...data... }
  createAndBindLoanToEstate(entity, reset: boolean = true): Observable<any> {
    return forkJoin(
      entity.loans && entity.loans.length
        ? entity.loans.map(loan =>
            this.loanDao.saveLoan(
              _.merge(loan, {
                accountId: entity.accoundId,
                subscriptionId: entity.subscriptionId,
              }),
              reset,
            ),
          )
        : of(true),
    );
  }

  locationEstateTreatment(entity, reset: boolean = true): Observable<any> {
    if (['lmplmnp', 'unfurnished'].includes(entity.typeUrl)) {
      entity.rents.forEach(r => (r.accountId = entity.accountId));

      return forkJoin([
        //Save fiscal law
        entity.fiscalLaw
          ? this.postFiscalLaw(
              entity.userId,
              entity.typeUrl,
              entity.id,
              new FiscalLaw(entity.fiscalLaw),
              false,
            )
          : of(null),
        //Post rents & tenant
        forkJoin(
          entity.rents && entity.rents.length
            ? entity.rents.map(rent =>
                this.postRealestateRent(
                  entity.userId,
                  entity.typeUrl,
                  entity.id,
                  _.omit(rent, ['tenant']),
                  reset,
                ).pipe(
                  switchMap(newRent =>
                    // Un bail existant envoyé sans locataire garde celui déjà enregistré
                    rent.id && rent.tenant === undefined
                      ? of(newRent)
                      : this.postRealestateTenant(
                          entity.userId,
                          entity.typeUrl,
                          entity.id,
                          newRent.data,
                          new CreateTenantDTO(
                            _.merge(rent.tenant, {
                              type: 'AGENCY',
                              accountId: entity.accoundId,
                            }),
                          ),
                          reset,
                        ),
                  ),
                ),
              )
            : of(true),
        ),
      ]);
    } else if (
      (entity.typeUrl === 'personalUse' ||
        entity.productType === 'PERSO_USE') &&
      entity.rents &&
      entity.rents.length
    ) {
      const typeUrl = entity.typeUrl || 'personalUse';
      entity.rents.forEach(r => (r.accountId = entity.accountId));

      return forkJoin(
        entity.rents.map(rent => {
          const rentPayload = {
            ..._(rent).omit(['contactTenant']).value(),
            // Inclure contactTenant formaté pour l'API backend
            contactTenant:
              rent.contactTenant &&
              (rent.contactTenant.firstName ||
                rent.contactTenant.lastName ||
                rent.contactTenant.email ||
                rent.contactTenant.phone)
                ? {
                    ...(rent.contactTenant.id
                      ? { id: rent.contactTenant.id }
                      : {}),
                    type: rent.contactTenant.type || 'INDIVIDUAL',
                    firstName: rent.contactTenant.firstName || '',
                    lastName: rent.contactTenant.lastName || '',
                    email: rent.contactTenant.email || '',
                    phone: rent.contactTenant.phone || '',
                  }
                : undefined,
          };

          return this.postRealestateRent(
            entity.userId,
            typeUrl,
            entity.id,
            rentPayload,
            reset,
          );
        }),
      );
    } else return of(true);
  }

  /* state */
  onUserChange(event: any) {
    this?.reset();
  }
}
